import React, { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'
import styles from './HorizontalItemScroller.module.css'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import classNames from 'classnames'
import Slider, { InnerSlider, Settings, ResponsiveObject } from 'react-slick'
import { ChevronLeftIcon } from '@/atoms/Icons/ChevronLeftIcon'
import { ChevronRightIcon } from '@/atoms/Icons/ChevronRightIcon'
import { MediaCardProps } from '@/molecules/MediaCard/MediaCard'
import { useSupportsTouch } from '@/utils/ScreenUtils'

interface ResponsiveSlideSettings {
  slidesToShow: number
  slidesToScroll: number
}

/**
 * Get the appropriate slide settings based on the current window width.
 * This replaces react-slick's built-in responsive handling which doesn't
 * properly initialize on mount in v0.31.0+ (addListener only fires on changes,
 * not on initial state).
 *
 * Breakpoints work as "max-width" - settings apply when viewport <= breakpoint.
 * We find the smallest breakpoint that the width still falls under.
 */
const getResponsiveSlideSettings = (
  width: number,
  responsive: ResponsiveObject[],
  defaults: ResponsiveSlideSettings,
): ResponsiveSlideSettings => {
  const sortedBreakpoints = responsive.toSorted((a, b) => a.breakpoint - b.breakpoint)

  for (const bp of sortedBreakpoints) {
    if (width <= bp.breakpoint && bp.settings !== 'unslick' && typeof bp.settings === 'object') {
      return {
        slidesToShow: bp.settings.slidesToShow ?? defaults.slidesToShow,
        slidesToScroll: bp.settings.slidesToScroll ?? defaults.slidesToScroll,
      }
    }
  }

  // Width is larger than all breakpoints - use the largest breakpoint's settings if available
  const largestBreakpoint = sortedBreakpoints[sortedBreakpoints.length - 1]
  if (
    largestBreakpoint?.settings &&
    largestBreakpoint.settings !== 'unslick' &&
    typeof largestBreakpoint.settings === 'object'
  ) {
    return {
      slidesToShow: largestBreakpoint.settings.slidesToShow ?? defaults.slidesToShow,
      slidesToScroll: largestBreakpoint.settings.slidesToScroll ?? defaults.slidesToScroll,
    }
  }

  return defaults
}

const defaultReactSlickResponsive: ResponsiveObject[] = [
  {
    breakpoint: 2900,
    settings: {
      slidesToScroll: 9,
      slidesToShow: 9,
    },
  },
  {
    breakpoint: 2560,
    settings: {
      slidesToScroll: 7,
      slidesToShow: 7,
    },
  },
  {
    breakpoint: 2048,
    settings: {
      slidesToScroll: 6,
      slidesToShow: 6,
    },
  },
  {
    breakpoint: 1792,
    settings: {
      slidesToScroll: 5,
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 1536,
    settings: {
      slidesToScroll: 4,
      slidesToShow: 4,
    },
  },
  {
    breakpoint: 1400,
    settings: {
      slidesToScroll: 3,
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 768,
    settings: {
      slidesToScroll: 2.5,
      slidesToShow: 2.5,
    },
  },
  {
    breakpoint: 640,
    settings: {
      slidesToScroll: 2,
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 480,
    settings: {
      slidesToScroll: 1.5,
      slidesToShow: 1.5,
    },
  },
]

export interface KeyedMediaCardProps extends MediaCardProps {
  key: string
  guid: string
}

interface ReactSlickArrowProps {
  currentSlide?: number
  slideCount?: number
  onClick?: () => void
  style?: Record<string, string | number>
}

interface InnerSliderWithPrivateVariables extends InnerSlider {
  props: Settings
}

export interface ArrowCompProps {
  hasItems: boolean
  style?: React.CSSProperties
  onClick?: () => void
}

export interface HorizontalItemScrollerProps extends Settings {
  wrapperClassName?: string
  scrollerClassName?: string
  selectedIndex?: number
  goTo?: boolean
  NextArrowComp?: React.FC<ArrowCompProps>
  PrevArrowComp?: React.FC<ArrowCompProps>
  nextArrowClassName?: string
  previousArrowClassName?: string
  slickRef?: RefObject<Slider | null>
  haltLazyLoad?: boolean
}

const getChevronClassNames = (visible: boolean, supportsTouch: boolean, className?: string) =>
  classNames(
    'cursor-pointer max-w-12 min-w-12',
    'h-full w-6 sm:w-8 md:w-12 xl:w-16',
    'z-10 absolute top-0',
    'items-center justify-center',
    getVisibilityClass(visible, supportsTouch),
    styles.chevronGroup,
    className,
  )

const getVisibilityClass = (isVisible: boolean, supportsTouch: boolean): string =>
  classNames('lg:!hidden', {
    '!hidden !lg:flex lg:group-hover:!flex': isVisible && supportsTouch,
    '!flex lg:group-hover:!flex': isVisible && !supportsTouch,
    '!hidden': !isVisible,
  })

// Default slides to show when width is larger than all breakpoints (ultra-wide screens > 2900px)
const DEFAULT_SLIDES_TO_SHOW = 10

export const HorizontalItemScroller: React.FC<HorizontalItemScrollerProps> = ({
  children,
  wrapperClassName,
  scrollerClassName,
  NextArrowComp,
  PrevArrowComp,
  nextArrowClassName,
  previousArrowClassName,
  responsive = defaultReactSlickResponsive,
  slidesToShow: slidesToShowProp,
  slidesToScroll: slidesToScrollProp,
  selectedIndex = 0,
  goTo = false,
  slickRef,
  haltLazyLoad = false,
  ...props
}) => {
  const supportsTouch = useSupportsTouch()
  const slider = useRef<Slider>(null)
  const hasItems = Boolean(React.Children.count(children) > 0)

  // Track window width for responsive slide calculations.
  // react-slick v0.31.0 uses matchMedia.addListener which only fires on state changes,
  // not on initial state, so responsive breakpoints aren't applied on first load.
  // We handle responsive logic ourselves instead.
  //
  // Initialized as null to match SSR (avoids hydration mismatch). useLayoutEffect
  // sets the real width synchronously before the browser paints, so the Slider
  // updates from the SSR default to the correct slidesToShow in the same frame.
  const [windowWidth, setWindowWidth] = useState<number | null>(null)

  useLayoutEffect(() => {
    setWindowWidth(window.innerWidth)
  }, [])

  useEffect(() => {
    let rafId: number | null = null
    const handleResize = () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        setWindowWidth(window.innerWidth)
      })
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [])

  const hasMounted = windowWidth !== null

  const defaultSlidesToShow = slidesToShowProp ?? DEFAULT_SLIDES_TO_SHOW
  const defaultSlidesToScroll = slidesToScrollProp ?? defaultSlidesToShow
  const { slidesToShow, slidesToScroll } = hasMounted
    ? getResponsiveSlideSettings(windowWidth, responsive, {
        slidesToShow: defaultSlidesToShow,
        slidesToScroll: defaultSlidesToScroll,
      })
    : { slidesToShow: defaultSlidesToShow, slidesToScroll: defaultSlidesToScroll }

  const infinite = props.infinite

  const ReactSlickChevronRight: React.FC<ReactSlickArrowProps> = (props) => {
    const { currentSlide, slideCount, onClick, style } = props
    const innerSlider = slider?.current?.innerSlider as InnerSliderWithPrivateVariables

    let canGo = hasItems
    if (
      !infinite &&
      innerSlider?.props?.slidesToShow &&
      slideCount &&
      currentSlide &&
      (slideCount <= innerSlider?.props?.slidesToShow || currentSlide >= slideCount - innerSlider?.props?.slidesToShow)
    ) {
      canGo = false
    }

    return NextArrowComp ? (
      <NextArrowComp hasItems={canGo} style={style} onClick={onClick} />
    ) : (
      <div
        className={getChevronClassNames(canGo, supportsTouch, classNames('block right-4 group', nextArrowClassName))}
        onClick={onClick}
      >
        <div
          className={classNames(
            'flex items-center justify-items-center',
            'bg-black/25',
            'p-3 rounded-full h-10 w-10',
            styles.chevronCircle,
            !supportsTouch ? 'flex' : 'hidden lg:flex',
          )}
        >
          <ChevronRightIcon className="pl-1" height={28} width={18} />
        </div>
      </div>
    )
  }

  const ReactSlickChevronLeft: React.FC<ReactSlickArrowProps> = (props) => {
    const { currentSlide, onClick, style } = props

    const canGo = hasItems && (infinite || Boolean(currentSlide && currentSlide > 0))

    return PrevArrowComp ? (
      <PrevArrowComp hasItems={canGo} style={style} onClick={onClick} />
    ) : (
      <div
        className={getChevronClassNames(canGo, supportsTouch, classNames('left-0', previousArrowClassName))}
        onClick={onClick}
      >
        <div
          className={classNames(
            'flex items-center justify-items-center',
            'bg-black/25',
            'p-3 rounded-full h-10 w-10',
            styles.chevronCircle,
            !supportsTouch ? 'flex' : 'hidden lg:flex',
          )}
        >
          <ChevronLeftIcon className="pr-1" size={28} />
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (goTo) {
      slider?.current?.slickGoTo(selectedIndex)
    }
  }, [goTo, selectedIndex])

  if (!hasMounted) {
    return (
      <div className="group">
        <div className={wrapperClassName}>
          <div className={classNames(styles.ssrGrid, scrollerClassName)}>{children}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="group">
      <div className={wrapperClassName}>
        <Slider
          className={scrollerClassName}
          swipe
          touchMove
          useCSS
          useTransform
          waitForAnimate
          adaptiveHeight={false}
          ref={typeof slickRef === 'undefined' ? slider : slickRef}
          initialSlide={0}
          infinite={false}
          swipeToSlide={false}
          touchThreshold={5}
          cssEase="ease"
          easing="easeQuadOut"
          lazyLoad={!haltLazyLoad ? 'progressive' : undefined}
          edgeFriction={0.35}
          centerPadding="50px"
          speed={300}
          slidesToShow={slidesToShow}
          slidesToScroll={slidesToScroll}
          nextArrow={<ReactSlickChevronRight onClick={slider?.current?.slickNext} />}
          prevArrow={<ReactSlickChevronLeft onClick={slider?.current?.slickPrev} />}
          {...props}
        >
          {children}
        </Slider>
      </div>
    </div>
  )
}
